import React, { useState, useEffect } from "react";
import DatePicker from "react-multi-date-picker";
import { DateObject } from "react-multi-date-picker";
import { FormGroup, Label, Card, Col } from "reactstrap";
import CommonCardHeader from "../../../Utils/CommonComponents/CommonCardHeader";
import CommonTable from "../../Tables/ReactstrapTables/BasicTables/Common/CommonTable";
import { baseUrl } from "../../../Utils/Baseurl/Baseurl";
import { toast } from "react-toastify";
import api from "../../../services/api";
import { Btn } from "../../../AbstractElements";

interface AssignedDate {
  id: number;
  dates: string;
}

export default function AssignedDates() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [assignedDates, setAssignedDates] = useState<AssignedDate[]>([]);
  const [editingDate, setEditingDate] = useState<{
    id: number;
    date: Date | null;
  } | null>(null);

  // fetch all appointment dates
  useEffect(() => {
    fetchAssignedDates();
  }, []);

  const fetchAssignedDates = async () => {
    try {
      const response = await api.get(`${baseUrl}assignedDates/getAllDates`);
      setAssignedDates(response.data);
    } catch (error) {
      toast.error("Failed to fetch assigned dates.");
    }
  };

    // edit dates
  const handleEditDateClick = (dateObj: AssignedDate) => {
    setEditingDate({ id: dateObj.id, date: new Date(dateObj.dates) });
  };

  const updateDate = async () => {
    if (!editingDate || !editingDate.date) {
      toast.error("Please select a new date.");
      return;
    }

    try {
      const formattedDate = editingDate.date.toISOString().split("T")[0];

      const response = await api.post(
        `${baseUrl}assignedDates/editDate/${editingDate.id}`,
        {
          date: formattedDate,
        }
      );

      toast.success(response.data.message);
      setEditingDate(null);
      fetchAssignedDates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update date.");
    }
  };

  const handleDateChange = (date: DateObject | null) => {
    setSelectedDate(date ? date.toDate() : null);
  };

//  add date
  const handleAddDate = async () => {
    try {
      if (!selectedDate) {
        toast.error("Please select a date.");
        return;
      }

      const formattedDate = selectedDate.toISOString().split("T")[0];

      const response = await api.post(`${baseUrl}assignedDates/addDate`, {
        date: formattedDate,
      });

      toast.success(response.data.message);
      fetchAssignedDates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to add date.");
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sortedDates = [...assignedDates].sort(
    (a, b) => new Date(a.dates).getTime() - new Date(b.dates).getTime()
  );

  const latestPastDateIndex = sortedDates
    .map((date) => new Date(date.dates))
    .reverse()
    .findIndex((date) => date < today);

  const latestPastDate =
    latestPastDateIndex !== -1
      ? sortedDates[sortedDates.length - 1 - latestPastDateIndex]
      : null;

  const firstUpcomingDateIndex = latestPastDate
    ? sortedDates.findIndex(
        (date) => new Date(date.dates) > new Date(latestPastDate.dates)
      )
    : -1;


  return (
    <>
      <Col sm={12}>
        <Card>
          <FormGroup
            className="d-flex align-items-center justify-content-start m-5"
            style={{ gap: "20px" }}
          >
            <Label className="mb-0" style={{ minWidth: "150px" }}>
              Appointment Date
            </Label>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              minDate={new Date()}
              highlightToday={false}
              className="form-control"
              style={{
                width: "250px",
                height: "45px",
                fontSize: "16px",
                padding: "10px",
              }}
              calendarPosition="bottom-center"
              containerStyle={{ width: "250px" }}
              render={(value, openCalendar) => (
                <input
                  className="form-control"
                  style={{ cursor: "pointer" }}
                  value={value}
                  onClick={openCalendar}
                  readOnly
                />
              )}
            />
            <button
              className="btn btn-success"
              style={{ height: "45px" }}
              onClick={handleAddDate}
            >
              Add
            </button>
          </FormGroup>

          <CommonCardHeader title="Assigned Dates" />
          <div className="table-responsive">
            <CommonTable
              headData={[
                { id: 1, head: "Sl.No" },
                { id: 2, head: "Date" },
                { id: 3, head: "Actions" },
              ]}
            >
              {sortedDates.map((date, index) => {
                const dateObject = new Date(date.dates);
                const isPastDate = dateObject < today;
                const isFirstUpcomingDate = index === firstUpcomingDateIndex;

                return (
                  <tr key={date.id}>
                    <td>{index + 1}</td>
                    <td>
                      {dateObject.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </td>
                    <td>
                      {isPastDate || isFirstUpcomingDate ? (
                        <span className="text-muted"></span>
                      ) : editingDate?.id === date.id ? (
                        <div className="d-flex align-items-center">
                          <DatePicker
                            value={editingDate.date}
                            onChange={(date) =>
                              setEditingDate((prev) =>
                                prev
                                  ? { ...prev, date: date?.toDate() || null }
                                  : null
                              )
                            }
                            format="YYYY-MM-DD"
                            minDate={new Date()}
                          />
                          <button
                            className="btn btn-success ms-2"
                            onClick={updateDate}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-secondary ms-2"
                            onClick={() => setEditingDate(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-warning"
                          onClick={() => handleEditDateClick(date)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </CommonTable>
          </div>
        </Card>
      </Col>

      <style>
        {`
          .rmdp-day.rmdp-disabled {
            color: #b0b0b0 !important;
            text-decoration: line-through;
            pointer-events: none;
          }
          .rmdp-day {
            color: #000 !important;
            font-weight: bold;
          }
          .rmdp-day.today {
            background-color: #008080 !important;
            color: white !important;
            border-radius: 50%;
          }
        `}
      </style>
    </>
  );
}
